import { useEffect, useState } from 'react';
import { MessageCircle, Mail, Link2, Share2, X, Check } from 'lucide-react';
import {
  canUseSystemShare,
  copyToClipboard,
  mailtoShareUrl,
  systemShare,
  whatsappShareUrl,
} from '../../utils/share';

export interface ShareSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  text: string;
  url?: string;
}

export function ShareSheet({ open, onClose, title, text, url }: ShareSheetProps) {
  const [copied, setCopied] = useState(false);
  const [systemAvailable] = useState(canUseSystemShare);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const fullText = url ? `${text}\n\n${url}` : text;

  const handleWhatsapp = () => {
    window.open(whatsappShareUrl(fullText), '_blank', 'noopener');
    onClose();
  };

  const handleEmail = () => {
    window.location.href = mailtoShareUrl(title, fullText);
    onClose();
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(url || text);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSystem = async () => {
    const ok = await systemShare({ title, text, url });
    if (ok) onClose();
  };

  const item =
    'flex items-center gap-3 w-full px-4 py-3.5 text-left rounded-xl transition-colors hover:bg-[var(--b2b-surface-muted)]';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4 no-print"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-[var(--b2b-border)] bg-[var(--b2b-surface)] shadow-xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Compartir presupuesto"
      >
        <div className="flex items-center justify-between gap-3 p-4 border-b border-[var(--b2b-border)]">
          <div className="min-w-0">
            <p className="font-semibold">Compartir</p>
            <p className="text-xs b2b-muted truncate">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-2">
          <button type="button" className={item} onClick={handleWhatsapp}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center bg-[#25D366]/15 text-[#25D366]">
              <MessageCircle className="w-5 h-5" />
            </span>
            <span className="font-medium">WhatsApp</span>
          </button>

          <button type="button" onClick={handleEmail} className={item}>
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[var(--b2b-accent-tint)] text-[var(--b2b-accent)]">
              <Mail className="w-5 h-5" />
            </span>
            <span className="font-medium">Correo electrónico</span>
          </button>

          <button type="button" onClick={handleCopy} className={item}>
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[var(--b2b-surface-muted)]">
              {copied ? <Check className="w-5 h-5 text-[color:var(--b2b-success)]" /> : <Link2 className="w-5 h-5" />}
            </span>
            <span className="font-medium">{copied ? '¡Enlace copiado!' : 'Copiar enlace'}</span>
          </button>

          {systemAvailable && (
            <button type="button" onClick={handleSystem} className={item}>
              <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--b2b-surface-muted)]">
                <Share2 className="w-5 h-5" />
              </span>
              <span className="font-medium">Más opciones…</span>
            </button>
          )}
        </div>

        <div className="p-3 pt-0">
          <button type="button" onClick={onClose} className="b2b-btn b2b-btn--ghost w-full justify-center">
            <X className="w-4 h-4" /> Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
